import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import LazySvg from 'components/LazySvg';
import { userEventTracking } from 'services/misc/miscAsync';
import { selectIsUserAgent } from 'services/session/sessionSelectors';
import { logErrors } from 'utils';
import { USER_EVENTS } from 'utils/constants';
import { QaUpsellNotification } from 'testdata/constants';
import styles from './index.module.scss';

/* Angular parity — `critical-notifications.component.ts:141`. Scoped to the
   browsing session so the impression is reported once per session, not once per
   dashboard visit. Stamped before the request rather than after (Angular's
   `:143` order) so a re-mount — React 18 StrictMode double-invokes effects in
   development — cannot report it twice. */
const UPSELL_DISPLAYED_KEY = 'upsellBannerDisplayed';

const PLAN_DETAILS_PATH = '/my-account/offers-and-upgrades';

type Props = {
  // eslint-disable-next-line no-unused-vars
  onDismiss: () => void;
};

/**
 * "Level up your identity theft protection with more features." — the upsell
 * banner of Angular's custom-notification tier
 * (`critical-notifications.component.html:16-24`).
 *
 * Eligibility is decided upstream by `selectIsEligibleForUpsellBanner`; this
 * component owns the banner's own behaviour — its impression event, and the two
 * ways out of it. Both exits dismiss for the rest of the session, differing only
 * in the event they report (`:145-150 dismissUpsellBanner`).
 */
function Upsell({ onDismiss }: Props) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  /* Angular gates every banner event on `!session.agentId`
     (`:152`) — an agent driving the account should not register as the member
     engaging with an upsell. */
  const isUserAgent = useAppSelector(selectIsUserAgent);

  const trackBannerEvent = async (eventType: string) => {
    if (isUserAgent) return;
    try {
      /* `userEventTracking` already stamps `sessionId` and `currentPlanCode`
         (`miscAsync.ts:215-236`), which is the rest of Angular's payload. */
      await dispatch(
        userEventTracking({ eventType, bannerName: `${eventType}_v1` }),
      );
    } catch (error) {
      logErrors(error);
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem(UPSELL_DISPLAYED_KEY) !== null) return;
    sessionStorage.setItem(UPSELL_DISPLAYED_KEY, 'true');
    trackBannerEvent(USER_EVENTS.displayUpsellBanner);
  }, []);

  /* Angular routes to plan details from inside the agent-guarded block
     (`:159-161`), which leaves an agent's click doing nothing at all. The
     navigation is the point of the banner, so it happens either way here and
     only the event is suppressed. */
  const handleUpgradeClick = () => {
    trackBannerEvent(USER_EVENTS.viewUpsellBanner);
    onDismiss();
    navigate(PLAN_DETAILS_PATH, { replace: true });
  };

  const handleClose = () => {
    trackBannerEvent(USER_EVENTS.dismissUpsellBanner);
    onDismiss();
  };

  return (
    <div
      className={styles.upsell}
      data-qa-selector={QaUpsellNotification.banner}
    >
      <div className={styles.upsellText}>
        <LazySvg
          name='info-tag'
          className={styles.tagIcon}
          aria-hidden='true'
        />
        <div>
          Level up your identity theft protection with more features.{' '}
          <button
            type='button'
            className={styles.upsellLink}
            onClick={handleUpgradeClick}
            data-qa-selector={QaUpsellNotification.linkUpgradePlan}
          >
            Upgrade your plan.
          </button>
        </div>
      </div>
      <button
        type='button'
        className={styles.upsellClose}
        aria-label='close'
        onClick={handleClose}
        data-qa-selector={QaUpsellNotification.buttonClose}
      >
        <LazySvg name='close' className={styles.closeGlyph} />
      </button>
    </div>
  );
}

export default Upsell;
